import { useGetPatientsVisited } from '@vindr/services/actions/patient.action';
import { INIT_PAGINATION } from '@vindr/services/constants/common.constant';
import { PaginationProps } from '@vindr/services/interfaces';
import Table from '@vindr/ui/src/components/ui/Table';
import onClickRow, { onDoubleClickRow } from '@vindr/utils/state/onClickRow';
import onPaginationChange from '@vindr/utils/state/onPaginationChange';
import { Button } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useImmer } from 'use-immer';
import Filter from './PatientFilter';
import usePatientColumns from './usePatientColumns';
import { getArrayVal } from '@vindr/utils/helpers/common.helper';
import { ResponseGetPatientsVisited } from '@vindr/services/apis/patient.api';

const PatientSearch = ({
  initFilters,
  onSave,
  onCancel,
}: {
  initFilters: Record<string, any>;
  onSave: (value: ResponseGetPatientsVisited['data'][0]) => void;
  onCancel: () => void;
}) => {
  const { t } = useTranslation('Vindoc');
  const [selectedRowKeys, setSelectedRowKeys] = useState<ResponseGetPatientsVisited['data']>([]);
  const columns = usePatientColumns();
  const [params, setParams] = useState(initFilters);
  const [pagination, setPagination] = useImmer<PaginationProps>(INIT_PAGINATION);
  const query = useGetPatientsVisited({
    ...params,
    limit: pagination.size,
    offset: (pagination.page - 1) * pagination.size,
  });
  const count = query?.data?.count;
  const patients = query?.data?.data || [];
  const isLoading = query?.isLoading;
  const isFetching = query?.isFetching;
  const refetch = query?.refetch;

  const [isRefetch, setIsRefetch] = useState(false);
  useEffect(() => {
    if (!isFetching) {
      // After fetching, set isRefetch to false;
      setIsRefetch(false);
    }
  }, [isFetching]);

  const forceSearch = (params) => {
    setParams(params);
    // Set refetch to true only when force search
    setIsRefetch(true);
    refetch?.();
  };

  return (
    <div className="flex flex-col">
      <Filter
        forceSearch={forceSearch}
        initFilters={initFilters}
        onChange={(p) => {
          setParams(p);
        }}
      />
      <Table
        columns={columns}
        count={count}
        dataSource={patients}
        loading={isLoading || isRefetch}
        pagination={count < pagination.size ? null : pagination}
        rowKey="id"
        scroll={{ y: 500 }}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        style={{ cursor: 'pointer' }}
        wrapperClassName="h-auto"
        onPaginationChange={(p) => {
          setPagination((draft) => onPaginationChange(draft, p));
        }}
        onRow={(record) => {
          return {
            onClick: (event) => onClickRow({ event, setSelectedRowKeys, record }),
            onDoubleClick: (event) =>
              onDoubleClickRow({
                event,
                callback: () => onSave(record),
              }),
          };
        }}
      />
      <div className="footer mt-3 flex justify-end space-x-1">
        <Button onClick={onCancel}>{t('Cancel')}</Button>
        <Button type="primary" onClick={() => onSave(getArrayVal(selectedRowKeys))}>
          {t('Save')}
        </Button>
      </div>
    </div>
  );
};

export default PatientSearch;
